import { getPlayer } from "@/api/privateRequests/players/getPlayer";
import { BestFormationFromBackendSquad } from "@/api/privateRequests/squad-builder/build-squad";
import {
  addPlayerOrChangeRole,
  importSquad,
  loadBestFormationSquad,
  updateChangingPitchItemOnIndex,
} from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { SquadBuilderPlayerType } from "@/redux/squadBuilder/squadBuilderUtils";
import { AppDispatch } from "@/redux/store";
import { EvoPathId } from "@repo/shared/types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { SquadSwap } from "./useActiveSquad";

/** What a recommended card carries about its evolution, if it is one. */
type EvolvedPlayer = {
  evoPathId?: EvoPathId;
  evolutionPath?: number[];
  pathwayPath?: number[][];
};

/**
 * Opens the squad builder on the active squad; with a swap, with the swap
 * already made, so a previewed upgrade carries over as it was on the pitch.
 *
 * The squad builder keeps its squad in the store, so the active squad is
 * loaded into it and the incoming player put in its slot before going there,
 * the way the evolutions builder hands a player over. The incoming card is
 * fetched in full first: a recommendation's card has no meta ratings or stats.
 * If any of that fails, the squad builder opens on the active squad alone.
 */
export const useOpenInSquadBuilder = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);

  /** `playerRoleId`: the slot's role, so the card comes with its ratings. */
  const open = async (swap?: SquadSwap, playerRoleId?: number) => {
    if (!swap) {
      navigate("/squad-builder?squad=active");
      return;
    }
    setOpening(true);
    try {
      const { resourceId } = swap.inPlayer;
      const { evoPathId, evolutionPath, pathwayPath } =
        swap.inPlayer as EvolvedPlayer;
      const [player] = await Promise.all([
        getPlayer({
          resourceId,
          playerRoleId,
          evoPathId,
          evolutionPath,
          pathwayPath,
          backendPlayerType: evolutionPath?.length ? "evo-finder" : "base",
        }),
        dispatch(importSquad()).unwrap(),
      ]);
      dispatch(updateChangingPitchItemOnIndex(swap.slotIndex));
      dispatch(
        addPlayerOrChangeRole({
          player: {
            ...player,
            resourceId,
            evoPathId,
            evolutionPath,
            pathwayPath,
          } as unknown as SquadBuilderPlayerType,
        }),
      );
      navigate("/squad-builder");
    } catch {
      navigate("/squad-builder?squad=active");
    } finally {
      // Whatever happened, the squad builder must not open mid-swap.
      dispatch(updateChangingPitchItemOnIndex(-1));
      setOpening(false);
    }
  };

  /*
    The best squad from the onboarding, as the squad builder's own Best
    formation build would leave it: the squad in its suggestions, picked, in
    its own formation and tactic.
  */
  const openBest = async (squad: BestFormationFromBackendSquad) => {
    await dispatch(loadBestFormationSquad(squad));
    navigate("/squad-builder");
  };

  return { open, openBest, opening };
};
